<!-- Hero Section -->
<section class="relative py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
  <div class="absolute inset-0 opacity-20">
    <img src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1920&h=800&fit=crop" alt="" class="w-full h-full object-cover">
  </div>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
    <div class="text-center text-white">
      <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Cloud Audits & Compliance</h1>
      <p class="text-xl text-white/90 max-w-3xl mx-auto mb-8">
        Navigate regulatory requirements with confidence. Expert compliance audits and certification services for PCI DSS, SOC 2, HIPAA, ISO 27001, and GDPR.
      </p>
      <a routerLink="/contact" class="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
        Get Your Compliance Assessment
      </a>
    </div>
  </div>
</section>

<!-- Stats Section -->
<section class="py-12 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
      @for (stat of stats; track stat.label) {
        <div class="text-center">
          <div class="text-3xl md:text-4xl font-bold text-primary-600 mb-2">{{ stat.value }}</div>
          <div class="text-gray-600">{{ stat.label }}</div>
        </div>
      }
    </div>
  </div>
</section>

<!-- Services Grid -->
<section class="py-20 bg-gray-50">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Compliance Services</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Comprehensive compliance solutions for every regulatory requirement.
      </p>
    </div>
    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      @for (service of services; track service.title) {
        <a [routerLink]="service.link" class="group card overflow-hidden hover:-translate-y-2 transition-all duration-300">
          <div class="h-48 overflow-hidden">
            <img [src]="service.image" [alt]="service.title" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500">
          </div>
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">{{ service.title }}</h3>
            <p class="text-gray-600 mb-4">{{ service.description }}</p>
            <span class="text-primary-600 font-medium flex items-center">
              Learn More
              <svg class="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </span>
          </div>
        </a>
      }
    </div>
  </div>
</section>

<!-- Case Studies Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Real compliance achievements from real clients.
      </p>
    </div>
    <div class="space-y-16">
      @for (study of caseStudies; track study.client; let i = $index) {
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div [class.lg:order-2]="i % 2 === 1">
            <img [src]="study.image" [alt]="study.client" class="rounded-2xl shadow-xl w-full">
          </div>
          <div [class.lg:order-1]="i % 2 === 1">
            <div class="inline-block px-4 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-medium mb-4">
              {{ study.industry }}
            </div>
            <h3 class="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{{ study.client }}</h3>
            <div class="space-y-4 mb-6">
              <div>
                <h4 class="font-semibold text-gray-900 mb-1">Challenge</h4>
                <p class="text-gray-600">{{ study.challenge }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-gray-900 mb-1">Solution</h4>
                <p class="text-gray-600">{{ study.solution }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-gray-900 mb-2">Results</h4>
                <ul class="space-y-2">
                  @for (result of study.results; track result) {
                    <li class="flex items-center text-gray-600">
                      <svg class="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                      </svg>
                      {{ result }}
                    </li>
                  }
                </ul>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</section>

<!-- Testimonials Section -->
<section class="py-20 bg-gradient-to-br from-primary-50 to-primary-100">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Trusted by security leaders worldwide.
      </p>
    </div>
    <div class="grid md:grid-cols-3 gap-8">
      @for (testimonial of testimonials; track testimonial.author) {
        <div class="bg-white rounded-2xl p-8 shadow-lg">
          <div class="flex mb-4">
            @for (star of [1,2,3,4,5]; track star) {
              <svg class="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            }
          </div>
          <p class="text-gray-600 mb-6 italic">"{{ testimonial.quote }}"</p>
          <div class="flex items-center">
            <img [src]="testimonial.image" [alt]="testimonial.author" class="w-12 h-12 rounded-full object-cover mr-4">
            <div>
              <div class="font-semibold text-gray-900">{{ testimonial.author }}</div>
              <div class="text-sm text-gray-500">{{ testimonial.role }}, {{ testimonial.company }}</div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
    <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Achieve Compliance?</h2>
    <p class="text-xl text-white/90 mb-8">
      Let's discuss your compliance requirements and create a customized roadmap.
    </p>
    <div class="flex flex-col sm:flex-row gap-4 justify-center">
      <a routerLink="/contact" class="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors">
        Schedule Consultation
      </a>
      <a href="tel:+15551234567" class="bg-white/10 text-white border border-white/30 px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors">
        Call Us: +1 (555) 123-4567
      </a>
    </div>
  </div>
</section>
